/* eslint-disable react/prop-types */
import React, { useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { VideoDataContext } from ".";
import { getVideoAPIAndDomain } from "./utils";
import { usePlaybackUniqueId } from "./usePlaybackUniqueId";
import {
  setInitialVideoState,
  videoApiReducer
} from "../../hooks/useVideoApi/reducer";
import { useVideoApi } from "../../hooks/useVideoApi";

export const VideoDataProvider = ({
  uuid,
  video: initialVideoData,
  videoStartId,
  isSandbox,
  config = {},
  orcaRef,
  children,
  useVideoApiOptions = {}
}) => {
  const { adOverrides: { domain: domainOverride } = {} } = config;

  const videoProp = useMemo(() => {
    return initialVideoData || {};
  }, [initialVideoData]);

  const { videoApiUrl, domain } = useMemo(() => {
    return getVideoAPIAndDomain(domainOverride, isSandbox);
  }, [domainOverride, isSandbox]);

  const [videoDataState, dispatchVideoApiEvent] = useReducer(
    videoApiReducer,
    setInitialVideoState(videoProp)
  );

  const {
    originalVideo,
    videoApiDataFailed,
    videoData,
    fetchedByUUID,
    isForceLoadVideo
  } = videoDataState;

  const deps = {
    dispatchVideoApiEvent,
    domain,
    originalVideoData: originalVideo,
    uuid,
    videoApiUrl,
    currentVideoData: videoData,
    propVideoData: videoProp,
    config,
    orcaRef
  };

  useVideoApi(deps, useVideoApiOptions);

  const videoId = useMemo(() => {
    return videoData?._id ? videoData._id : uuid;
  }, [videoData?._id, uuid]);

  const externalVideoStartId = videoStartId ?? null;
  const { uniqueId, setVideoStartId, resetUniqueId } = usePlaybackUniqueId({
    videoStartId: externalVideoStartId,
    videoId
  });

  const contextValue = useMemo(
    () => ({
      originalVideo,
      videoApiDataFailed,
      videoData,
      videoId,
      dispatchVideoApiEvent,
      videoApiUrl,
      domain,
      fetchedByUUID,
      uniqueId,
      isForceLoadVideo,
      resetUniqueId,
      setVideoStartId
    }),
    [
      domain,
      originalVideo,
      videoApiDataFailed,
      videoApiUrl,
      videoData,
      videoId,
      fetchedByUUID,
      uniqueId,
      isForceLoadVideo,
      resetUniqueId,
      setVideoStartId
    ]
  );

  return (
    <VideoDataContext.Provider value={contextValue}>
      {children}
    </VideoDataContext.Provider>
  );
};

VideoDataProvider.propTypes = {
  video: PropTypes.object,
  domainOverride: PropTypes.string,
  isSandbox: PropTypes.bool,
  debug: PropTypes.bool,
  uuid: PropTypes.string,
  videoStartId: PropTypes.string,
  useVideoApiOptions: PropTypes.object,
  children: PropTypes.node
};
